package com.arena_web.data.http

import com.arena_web.core.config.AppConfig
import com.arena_web.core.config.Environment
import com.arena_web.core.logger.getLogger
import com.arena_web.data.dto.RefreshTokenRequest
import com.arena_web.data.dto.RefreshTokenResponse
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

const val PLATFORM_HEADER = "X-Platform"
const val PLATFORM_VALUE = "web"

object HttpClientFactory {
    private val logger = getLogger<HttpClientFactory>()

    private val jsonConfig = Json {
        ignoreUnknownKeys = true
        isLenient = true
        encodeDefaults = true
        explicitNulls = false
        prettyPrint = AppConfig.environment == Environment.DEVELOPMENT
    }

    fun createDefault(): HttpClient {
        return HttpClient(Js) {
            install(ContentNegotiation) { json(jsonConfig) }
            installLogging()
            defaultRequest {
                header(PLATFORM_HEADER, PLATFORM_VALUE)
                url { parameters.append("_", cacheBuster()) }
            }
            expectSuccess = false
        }
    }

    fun createAuthenticated(token: String): HttpClient {
        val client = HttpClient(Js) {
            install(ContentNegotiation) { json(jsonConfig) }
            install(HttpSend) { maxSendCount = 2 }
            installLogging()

            defaultRequest {
                header("Content-Type", "application/json")
                header("Accept", "application/json")
                header(PLATFORM_HEADER, PLATFORM_VALUE)
                url { parameters.append("_", cacheBuster()) }
                val currentToken = Storage.get(StorageKeys.ACCESS_TOKEN) ?: token
                header("Authorization", "Bearer $currentToken")
            }

            expectSuccess = false
        }

        client.plugin(HttpSend).intercept { request ->
            val originalCall = execute(request)
            if (originalCall.response.status == HttpStatusCode.Unauthorized) {
                val newToken = tryRefreshToken()
                if (newToken != null) {
                    request.headers.remove(HttpHeaders.Authorization)
                    request.headers.append(HttpHeaders.Authorization, "Bearer $newToken")
                    execute(request)
                } else {
                    originalCall
                }
            } else {
                originalCall
            }
        }

        return client
    }

    private suspend fun tryRefreshToken(): String? {
        val refreshToken = Storage.get(StorageKeys.REFRESH_TOKEN) ?: return null
        return try {
            val refreshClient = createDefault()
            val response = refreshClient.post(AppConfig.getApiUrl("v1/auth/refresh")) {
                contentType(ContentType.Application.Json)
                setBody(RefreshTokenRequest(refreshToken))
            }
            refreshClient.close()

            val apiResponse = response.body<ApiResponse<RefreshTokenResponse>>()
            apiResponse.data?.let {
                Storage.save(StorageKeys.ACCESS_TOKEN, it.accessToken)
                Storage.save(StorageKeys.REFRESH_TOKEN, it.refreshToken)
                logger.debug("Token refreshed successfully")
                it.accessToken
            }
        } catch (e: Exception) {
            logger.debug("Token refresh failed: ${e.message}")
            null
        }
    }

    private fun cacheBuster(): String = js("Date.now().toString()") as String

    private fun HttpClientConfig<*>.installLogging() {
        if (AppConfig.environment == Environment.DEVELOPMENT) {
            install(Logging) {
                level = LogLevel.ALL
                logger = object : io.ktor.client.plugins.logging.Logger {
                    override fun log(message: String) {
                        this@HttpClientFactory.logger.debug(message)
                    }
                }
            }
        }
    }
}

fun HttpRequestBuilder.addDefaultHeaders() {
    header("Content-Type", "application/json")
    header("Accept", "application/json")
    header(PLATFORM_HEADER, PLATFORM_VALUE)
}

fun HttpRequestBuilder.addPlatformHeader() {
    header(PLATFORM_HEADER, PLATFORM_VALUE)
}

fun HttpRequestBuilder.addAuthHeader(token: String) {
    header("Authorization", "Bearer $token")
}

fun HttpRequestBuilder.setBaseUrl(baseUrl: String) {
    val currentUrl = url.buildString()
    if (!currentUrl.startsWith(baseUrl)) {
        url(baseUrl)
    }
}
